var React = require('react');
var { startTransition } = require('react');
var { connect } = require('react-redux');
var fieldManager = require('fieldManager');
var distributionActions = require('distributionActions');
var { withTranslation } = require('react-i18next');
const { unitDisplayName } = require('unitsManager');
var { sortByAlphabetic, sortArrayByValue } = require('helpers');
var FieldSelectionRow = require('FieldSelectionRow');
var rolesManager = require('@managers/rolesManager');
var { buildFieldsCacheKey } = require('./fieldsSelectorHelpers');

class FieldsSelector extends React.Component {
  constructor(props) {
    super(props);

    this.handleFieldSelection = this.handleFieldSelection.bind(this);
    this.handleDuplicateField = this.handleDuplicateField.bind(this);
    this.setClusterDisplay = this.setClusterDisplay.bind(this);

    this.state = {
      optimisticFieldId: null,
    };
    this._fieldsCacheKey = null;
    this._cachedFieldsData = null;
  }

  componentWillUnmount() {}

  componentDidUpdate(prevProps) {
    if (
      prevProps.loadHistoryStatus?.status !==
      this.props.loadHistoryStatus?.status
    ) {
      this._fieldsCacheKey = null;
    }
    if (
      this.state.optimisticFieldId &&
      this.props.selectedField?.id === this.state.optimisticFieldId
    ) {
      this.setState({ optimisticFieldId: null });
    }
  }

  handleFieldSelection(fieldId) {
    if (this.props.selectedField && this.props.selectedField.id !== fieldId) {
      // Skeleton farming units have no `fields` yet — ignore until the full tree lands.
      var selectedField = this.props.selectedFarmingUnit?.fields?.[fieldId];
      if (!selectedField) {
        return;
      }

      this.setState({ optimisticFieldId: fieldId });

      mixpanel.track('Field Change', {
        'Field ID': selectedField.id,
        'Field Name': selectedField.name,
        Source: 'Field View',
        Component: 'List',
      });
      startTransition(() => {
        this.props.setSelectedEntities(null, null, selectedField, null);
      });
    }
  }

  handleDuplicateField(currentField) {
    mixpanel.track('Duplicate Field', {
      'Field ID': currentField.id,
      'Field Name': currentField.name,
    });
  }

  setClusterDisplay(currentFieldCluster, fieldIndex, lastFieldCluster) {
    const { t } = this.props;
    let showCluster = true;
    let clusterString = '';

    if (currentFieldCluster === '') {
      clusterString = t('no_cluster').toUpperCase();
    } else {
      clusterString = (t('cluster') + ' ' + currentFieldCluster).toUpperCase();
    }

    if (fieldIndex !== 0 && currentFieldCluster === lastFieldCluster) {
      clusterString = undefined;
    }
    return clusterString;
  }

  computeFieldsData(userLengthUnits, userPressureUnits) {
    // Include a hash of which fields have historical data to invalidate cache
    // when history loads (loadHistoryStatus.status cycles back to '' after loading)
    const distId_ = this.props.selectedDistribution?.id;
    const fuId_ = this.props.selectedFarmingUnit?.id;
    const fields_ =
      this.props.distributions?.[distId_]?.farming_units?.[fuId_]?.fields;
    // Key also hashes each field's name + cluster so an optimistic rename
    // (which patches `distributions` but not history) invalidates the memo
    // immediately, instead of the list staying stale until the ~8s background
    // refetch flips loadHistoryStatus (N-181).
    const cacheKey = buildFieldsCacheKey({
      farmingUnitId: fuId_,
      sortBy: this.props.sortBy,
      historyStatus: this.props.loadHistoryStatus?.status,
      fieldState: this.props.fieldState,
      fields: fields_,
    });

    if (this._fieldsCacheKey === cacheKey && this._cachedFieldsData) {
      return this._cachedFieldsData;
    }

    // Read fields from distributions (authoritative source) rather than
    // selected_entities, because historical data updates write to distributions
    // but don't update selected_entities. Skeleton FUs omit `fields` entirely.
    const distId = this.props.selectedDistribution?.id;
    const fuId = this.props.selectedFarmingUnit?.id;
    const fieldsSource =
      this.props.distributions?.[distId]?.farming_units?.[fuId]?.fields ||
      this.props.selectedFarmingUnit?.fields;
    if (!fieldsSource) {
      return [];
    }

    let allFields = Object.values(fieldsSource);
    let formattedFields = allFields
      .filter((currentField) => currentField.settings.field_state === 'active')
      .map((currentField) => {
        var fieldItems = fieldManager.getFieldItems(currentField);
        // Crop water availability is shown for every planner state, so the v2
        // stress levels drive the colors for all fields.
        var fieldStressColor =
          fieldItems.irrigationDataItem.value == -1.0
            ? 'rgb(195, 201, 215)'
            : fieldManager.get_stress_color(
                fieldItems.stressDataItem.value,
                true,
              );
        var fieldStressBackgroundColor =
          fieldItems.irrigationDataItem.value == -1.0
            ? 'rgb(195, 201, 215)'
            : fieldManager.get_stress_background_color(
                fieldItems.stressDataItem.value,
                true,
              );

        return {
          name: currentField.name,
          ir: fieldItems.irrigationDataItem.value,
          id: currentField.id,
          stressLevel: fieldItems.stressDataItem.value,
          stressColor: fieldStressColor,
          stressBackgroundColor: fieldStressBackgroundColor,
          accumulatedETc: fieldItems.accumulatedETc.value,
          userLengthUnits: userLengthUnits,
          userPressureUnits: userPressureUnits,
          wpSamples: currentField.historical_data?.wp_samples,
          timeZone: currentField.geo_data.time_zone,
          cluster: currentField.cluster.id,
          plannerType: currentField.settings?.planner_settings?.type,
        };
      });

    if (this.props.sortBy === 'deficit') {
      formattedFields.sort(fieldManager.compareFields);
    } else if (this.props.sortBy === 'alphabetic') {
      sortByAlphabetic(formattedFields, 'name');
    } else if (this.props.sortBy === 'cluster') {
      // Tie-break by name so order stays stable across edits/refreshes
      formattedFields.sort((a, b) => {
        if (a.cluster !== b.cluster) {
          return a.cluster > b.cluster ? 1 : -1;
        }
        return (a.name || '').localeCompare(b.name || '');
      });
    }

    this._fieldsCacheKey = cacheKey;
    this._cachedFieldsData = formattedFields;
    return formattedFields;
  }

  render() {
    // Skeleton farming units (structural load only) have no `fields` key —
    // treat as empty until the full distribution tree hydrates.
    const farmingUnitFields = this.props.selectedFarmingUnit?.fields;
    const { t } = this.props;
    let userLengthUnits =
      this.props.userData && 'length' in this.props.userData
        ? unitDisplayName(this.props.userData.length)
        : '';
    let userPressureUnits =
      this.props.userData && 'pressure' in this.props.userData
        ? unitDisplayName(this.props.userData.pressure)
        : '';
    let formattedFields = undefined;
    let is_admin = false;
    let is_moderator = false;
    let isNdripUser = false;
    if (this.props.selectedFarmingUnit && farmingUnitFields) {
      formattedFields = this.computeFieldsData(
        userLengthUnits,
        userPressureUnits,
      );

      is_admin = this.props.selectedFarmingUnit.role === 'admin';
      is_moderator = this.props.selectedFarmingUnit.role === 'moderator';
      isNdripUser = rolesManager.isNdripUser(this.props.user_details?.email);
    }
    var show_report_issue = isNdripUser;

    return (
      <div
        className='d-flex flex-column  h-100'
        style={{
          boxSizing: 'border-box',
          borderRight: '1px solid var(--color-grey-91)',
        }}
      >
        <div>
          <div className='container-fluid p-0 bg-white'>
            <div
              className='row justify-content-between align-items-center m-0 pt-2 pb-2'
              style={{
                borderBottom: '1px solid #dee2e6',
                borderTop: '1px solid #dee2e6',
                flexWrap: 'nowrap',
              }}
            >
              <div
                className='text-grey71 text-size-12'
                style={{
                  width: '123px',
                  marginLeft: '16px',
                  marginRight: '16px',
                  lineHeight: '120%',
                }}
              >
                {t('field').toUpperCase()}
                <br />
                {t('name').toUpperCase()}
              </div>
              <div
                className='text-grey71 text-size-12'
                style={{
                  width: '78px',
                  marginRight: '16px',
                  lineHeight: '120%',
                }}
              >
                {t('gauge_title_v3').toUpperCase()}
              </div>
              <div
                className='text-grey71 text-size-12'
                style={{
                  width: '100px',
                  marginRight: '6px',
                  lineHeight: '120%',
                }}
              >
                {t('water_potential').toUpperCase()}
                {` (${userPressureUnits})`}
              </div>
            </div>
          </div>
        </div>
        <div
          className='bg-white flex-fill'
          style={{
            overflow: 'auto',
            borderBottom: '1px solid #dee2e6',
            overflowX: 'hidden',
          }}
        >
          {formattedFields &&
            formattedFields.map((currentField, index) => {
              let clusterString = this.setClusterDisplay(
                currentField?.cluster,
                index,
                formattedFields[index - 1]?.cluster,
              );
              const activeId =
                this.state.optimisticFieldId ?? this.props.selectedField?.id;
              let isActive = currentField.id === activeId;

              return (
                <div key={currentField.id}>
                  {clusterString !== undefined &&
                    this.props.sortBy === 'cluster' && (
                      <ClusterRow clusterString={clusterString} />
                    )}
                  <FieldSelectionRow
                    fieldData={currentField}
                    isActive={isActive}
                    isAdmin={is_admin}
                    isModerator={is_moderator}
                    showReportIssue={show_report_issue}
                    distributionId={this.props.selectedDistribution?.id}
                    distributionName={this.props.selectedDistribution?.name}
                    farmingUnitId={this.props.selectedFarmingUnit?.id}
                    farmingUnitName={this.props.selectedFarmingUnit?.name}
                    userName={
                      this.props.user_details?.first_name ||
                      this.props.user_details?.last_name
                        ? `${this.props.user_details?.first_name ?? ''} ${this.props.user_details?.last_name ?? ''}`.trim()
                        : undefined
                    }
                    userEmail={this.props.user_details?.email}
                    farmingUnitUserEmails={
                      this.props.farmingUnitRoles?.roles
                        ? Object.keys(this.props.farmingUnitRoles.roles)
                        : []
                    }
                    onSelect={this.handleFieldSelection}
                    onDuplicate={this.handleDuplicateField}
                    t={t}
                  />
                </div>
              );
            })}
        </div>
        {is_admin ? (
          <div className='bg-white' style={{ minHeight: '115px' }}>
            <div
              className='p-0 pt-2'
              style={{ marginTop: '8px', marginRight: '16px' }}
            >
              <div className='col-12 text-right pr-0'>
                <button
                  className='btn addBtn font-weight-bold text-grey45 pt-1 pb-1 pl-3 pr-3 text-size-16'
                  style={{
                    borderRadius: '3px',
                    border: '1px solid #919DB4',
                    width: '100%',
                  }}
                  data-toggle='modal'
                  data-target={'#' + 'fieldModalId'}
                  data-id={''}
                >
                  <img
                    src={require('images/icons/plus_icon.svg')}
                    className='img-fluid'
                    alt='irrigation ok'
                  />
                  <span className='ml-2'>{t('dashboard_add_field')}</span>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className='bg-white' style={{ paddingBottom: '36px' }} />
        )}
      </div>
    );
  }
}

class ClusterRow extends React.Component {
  render() {
    const { clusterString } = this.props;
    return (
      <div
        className='list-group-item m-0 p-0 pt-1'
        style={{
          borderRadius: '0%',
          border: '0px solid white',
          height: '39px',
          backgroundColor: '#FAFBFC',
          borderBottom: '1px solid #EEF1F6',
          display: 'flex',
        }}
      >
        <span
          style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#919DB4',
            paddingBottom: '8px',
            paddingLeft: '16px',
            paddingTop: '10px',
          }}
        >
          {clusterString}
        </span>
      </div>
    );
  }
}

const mapStateToProps = function (state) {
  return {
    selectedField: state.distribution_data.selected_entities.field,
    selectedFarmingUnit: state.distribution_data.selected_entities.farming_unit,
    selectedDistribution:
      state.distribution_data.selected_entities.distribution,
    userData: state.authentication_data.user_data,
    user_details: state.authentication_data.user_details,
    distributions: state.distribution_data.distributions?.distributions,
    loadHistoryStatus: state.distribution_data.farming_unit_load_history,
    fieldState:
      state.distribution_data?.selected_entities?.field?.settings?.field_state,
    farmingUnitRoles: state.roles_data?.roles?.selected_farming_unit_roles,
  };
};

const mapDispatchToProps = {
  setSelectedEntities: distributionActions.selected_entities,
};

module.exports = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(FieldsSelector));
